/**
 * @module LevelManager 
 */

import Level from "./level.js";

/**
 * Manager for Gamefroot-style level ordering.
 * @class LevelManager
 */
class LevelManager {

    constructor () {
        /**
         * Key of the currently running level.
         * @type {String}
         */
        this.activeLevel = null;

        /**
         * Target Phaser game. This must be set externally.
         * @type {Phaser.Game}
         */
        this.game = null;

        /**
         * Ordered list of levels in the current game.
         */
        this.levels = [];

        /**
         * Whether we have committed to changing the level.
         */
        this.switching = false;
    }

    /**
     * Create a level based on cached data under the given key.
     * Add the level to the tracking list.
     * @param {Object} json - data describing the new level
     * @param {number} index - number of the level
     */
    add( json, index ) {
        const key = "level-" + index;

        const level = new Level( { key, json } );
        this.game.scene.add( key, level, false );
        this.levels.push( level );
    }

    /**
     * Return the current scene.
     * @returns {?Phaser.Scene}
     */
    getCurrent() {
        return this.game.scene.getScene( this.activeLevel );
    }

    /**
     * Return the indicated scene.
     * @param {string|number} key - level identifier, either a key or
     *  a numerical index starting from 1
     * @returns {?Phaser.Scene}
     */
    getLevel( key ) {
        if ( typeof key === "number" ) {
            key = "level-" + key;
        }
        return this.game.scene.getScene( key );
    }

    /**
     * Switch to the indicated level at the end of the current frame.
     * It will be restarted, to clear all contents and changes from prior play.
     * Further calls to `switchTo()` will be ignored until the level switches.
     * @param {Number|String} level - target level index or key;
     *  index starts at 1.
     */
    switchTo( level ) {

        // Lock in switch.
        if ( this.switching ) {
            return;
        }
        this.switching = true;

        // Identify target level
        if ( typeof level === "number" ) {
            level = "level-" + level;
        }

        const currentScene = this.getCurrent();
        if ( currentScene ) {
            currentScene.events.once( "postupdate", () => {
                this._switchLevel( level );
            } );
        } else {
            this._switchLevel( level );
        }
    }

    /**
     * Switch to the indicated level.
     * @param {Number|String} level - target level index or key;
     *  index starts at 1.
     * @private
     */
    _switchLevel( level ) {
        const currentScene = this.getCurrent();
        if ( currentScene ) {
            currentScene.onSwitch( level );
        }

        this.switching = false;

        // Identify current level
        const currentLevel = this.activeLevel || this.game.scene.scenes[ 0 ];

        // Do the switch.
        // We catch potential errors because we can't guarantee perfect
        // data and perfect results.
        try {
            this.game.scene.stop( currentLevel );
            this.activeLevel = level;
            this.game.scene.start( level );
        } catch ( error ) {
            // Provide user-facing error.
            console.error(
                "LevelManager.switchTo() error", "target", level, error );
            const errorText = "Could not switch to a new level.\n" +
                "\n" +
                "Please try to load this game again. " +
                    "If this does not work, check your level and scripts. " +
                    "Ensure that they are free of errors and are not named " +
                    "with special characters.\n" +
                "\n" +
                "Target level number: " + level;
            this.game.reportError( errorText );
        }
    }
}

export default new LevelManager();
